/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import is_iterable from '../is/is_iterable';
import is_iterator from '../is/is_iterator';
import is_function from '../is/is_function';
import is_record from '../is/is_record';
import is_array from '../is/is_array';
import { TIterable, TGenerator } from '../types';

export interface Values {
  /**
   * pull the values from an object
   *
   * @public
   * @function f1.values
   * @param values
   * @example
   *
   * ```ts
   * f1.values({ a: 1, b: 2 }) => [1, 2]
   * f1.values([1, 2]) => [1, 2]
   * f1.values(1) => [1]
   * f1.values(Map{ t => 1, g => 2 }) => [1, 2]
   * f1.values(Set{ 3, 4 }) => [3, 4]
   * f1.values(null) => []
   * f1.values(-- iterator of [k1, v1], [k2, v2] --) => [v1, v2]
   *
   * // Node List
   * f1.values(document.querySelectorAll('...')) => [...]
   * // HTML Collection
   * f1.values(document.getElementsByClassName('...')) => [...]
   * ```
   */
  <V>(iter: V | TGenerator<V> | TIterable<V> | Record<string, V> | ArrayLike<V> | Map<never, V> | Set<V>): V[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function iterate<V>(iter: any): V[] {
  const rtn: V[] = [];

  for (const val of iter) {
    rtn.push(val);
  }

  return rtn;
}

/**
 * @see Iterate
 */
export default <V>(
  iter: V | TGenerator<V> | TIterable<V> | Record<string, V> | ArrayLike<V> | Map<unknown, V> | Set<V>
): V[] => {
  iter = is_function(iter) ? iter() : iter;

  return iter == null
    ? []
    : is_record(iter)
    ? (Object.values(iter) as V[])
    : is_array(iter)
    ? (iter as V[])
    : is_iterable<V>(iter) && 'values' in iter && typeof iter['values'] === 'function'
    ? Array.from(iter.values())
    : is_iterable<V>(iter)
    ? Array.from(iter)
    : is_iterator(iter)
    ? iterate<V>(iter as TIterable<V>)
    : [iter as V];
};
